
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/context/LanguageContext';
import { countries } from '@/utils/countries';
import { ContactFormData } from '@/schemas/ContactFormSchema';
import '@/styles/emoji-flags.css';

interface ContactFormFieldsProps {
  form: UseFormReturn<ContactFormData>;
  messageLength: number;
  maxMessageLength: number;
  onMessageChange: (length: number) => void;
}

export const ContactFormFields: React.FC<ContactFormFieldsProps> = ({
  form,
  messageLength,
  maxMessageLength,
  onMessageChange
}) => {
  const { t } = useLanguage();
  
  // Get sorted countries for the dropdowns
  const sortedCountries = React.useMemo(() => {
    return [...countries].sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-200">{t('contactMe.name')}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t('contactMe.placeholders.name')} 
                  {...field} 
                  className="bg-gray-800/70 border-gray-700 text-white placeholder:text-gray-400"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-200">{t('contactMe.lastName')}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t('contactMe.placeholders.lastName')} 
                  {...field} 
                  className="bg-gray-800/70 border-gray-700 text-white placeholder:text-gray-400"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-gray-200">{t('contactMe.email')}</FormLabel>
            <FormControl>
              <Input 
                placeholder={t('contactMe.placeholders.email')} 
                {...field} 
                className="bg-gray-800/70 border-gray-700 text-white placeholder:text-gray-400"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="country"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-gray-200">{t('contactMe.country')}</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger translate="no" className="bg-gray-800/70 border-gray-700 text-white">
                  <SelectValue placeholder={t('contactMe.placeholders.country')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="max-h-[300px] z-[9999] overflow-y-auto bg-gray-800 border-gray-700 text-white">
                {sortedCountries.map((country) => (
                  <SelectItem key={country.code} value={country.code} className="text-white focus:bg-gray-700 focus:text-white">
                    <span className="flex items-center">
                      <span className="mr-2 emoji-flag">{country.flag}</span>
                      {country.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="whatsapp"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-gray-200">{t('contactMe.whatsappNumber')}</FormLabel>
            <FormControl>
              <Input 
                placeholder={t('contactMe.placeholders.whatsappNumber')} 
                {...field} 
                className="bg-gray-800/70 border-gray-700 text-white placeholder:text-gray-400"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="language"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-gray-200">{t('contactMe.language')}</FormLabel>
            <FormControl>
              <Input 
                placeholder={t('contactMe.placeholders.language')}
                {...field} 
                className="bg-gray-800/70 border-gray-700 text-white placeholder:text-gray-400"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="message"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-gray-200">{t('contactMe.message')}</FormLabel>
            <FormControl>
              <Textarea 
                placeholder={t('contactMe.placeholders.message')}
                className="min-h-[80px] max-h-[80px] bg-gray-800/70 border-gray-700 text-white placeholder:text-gray-400"
                {...field}
                onChange={(e) => {
                  // Limit input to max length
                  const value = e.target.value;
                  if (value.length <= maxMessageLength) {
                    field.onChange(e);
                    onMessageChange(value.length);
                  }
                }}
                maxLength={maxMessageLength}
              />
            </FormControl>
            <div className="flex justify-end mt-1 text-sm text-gray-400">
              <span className={messageLength === maxMessageLength ? "text-amber-400" : ""}>
                {messageLength}/{maxMessageLength}
              </span>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
};
